import { forwardRef, useCallback, useId, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from "react";
import { X, Upload } from "lucide-react";
import { useControlledState } from "./hooks";
import { IconButton } from "./IconButton";
import { ProgressBar } from "./ProgressBar";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <FileUpload>. Drag/drop,
// click-to-browse, accept/maxSize filtering, and CSS ported letter-for-
// letter from the vendor's compiled bundle and its FileUpload CSS — new
// .rc-fileupload class names. Uses the local ./hooks' useControlledState
// (ported from @datasirpi/hooks, since fully removed from this app now)
// and the local ./IconButton sibling for the per-file remove button.
// Close icon switched to lucide's X, same reasoning as Checkbox/Stepper.
export interface FileUploadProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange" | "children" | "defaultValue"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  value?: File[];
  defaultValue?: File[];
  onChange?: (files: File[]) => void;
  onReject?: (rejected: Array<{ file: File; reason: string }>) => void;
  disabled?: boolean;
  /**
   * design.md ss30: avoid full-page spinners; loading states should be
   * visible. When true, replaces the dropzone's normal content with a
   * visible, indeterminate loading state (no byte-level progress is
   * available from a plain busy/not-busy upload call, so this is
   * intentionally indeterminate rather than a percentage bar).
   */
  uploading?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

function matchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept) return true;
  const types = accept.split(",").map((s) => s.trim()).filter(Boolean);
  return types.some((t) => {
    if (t.startsWith(".")) return file.name.toLowerCase().endsWith(t.toLowerCase());
    if (t.endsWith("/*")) return file.type.startsWith(t.slice(0, -1));
    return file.type === t;
  });
}

export const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(function FileUpload(
  {
    label = "Drop files here or click to browse",
    hint,
    error,
    accept,
    multiple = false,
    maxSize,
    value: controlled,
    defaultValue,
    onChange,
    onReject,
    disabled = false,
    uploading = false,
    className,
    id: providedId,
    ...rest
  },
  ref
) {
  const fallbackId = useId();
  const id = providedId ?? fallbackId;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [maybeFiles, setFiles] = useControlledState<File[]>({
    ...(controlled !== undefined ? { value: controlled } : {}),
    defaultValue: defaultValue ?? [],
    ...(onChange ? { onChange } : {}),
  });
  const files = useMemo(() => maybeFiles ?? [], [maybeFiles]);

  const acceptFiles = useCallback(
    (incoming: FileList) => {
      const list = Array.from(incoming);
      const accepted: File[] = [];
      const rejected: Array<{ file: File; reason: string }> = [];
      for (const f of list) {
        if (!matchesAccept(f, accept)) {
          rejected.push({ file: f, reason: "type" });
          continue;
        }
        if (maxSize !== undefined && f.size > maxSize) {
          rejected.push({ file: f, reason: "size" });
          continue;
        }
        accepted.push(f);
      }
      if (rejected.length > 0) onReject?.(rejected);
      if (accepted.length === 0) return;
      const next = multiple ? [...files, ...accepted] : accepted.slice(0, 1);
      setFiles(next);
    },
    [accept, maxSize, multiple, files, setFiles, onReject]
  );

  function openPicker() {
    if (disabled || uploading) return;
    inputRef.current?.click();
  }
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (disabled || uploading) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      acceptFiles(e.dataTransfer.files);
    }
  }
  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (disabled || uploading) return;
    if (!dragging) setDragging(true);
  }
  function onDragLeave() {
    setDragging(false);
  }
  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) acceptFiles(e.target.files);
    e.target.value = "";
  }
  function removeAt(idx: number) {
    setFiles(files.filter((_, i) => i !== idx));
  }
  function onKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (disabled || uploading) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  }

  return (
    <div ref={ref} className={cn("rc-fileupload", className)} {...rest}>
      <div
        role="button"
        tabIndex={disabled || uploading ? -1 : 0}
        aria-disabled={disabled || uploading || undefined}
        aria-busy={uploading || undefined}
        aria-controls={`${id}-list`}
        data-dragging={dragging || undefined}
        data-disabled={disabled || undefined}
        data-uploading={uploading || undefined}
        className="rc-fileupload__dropzone"
        onClick={openPicker}
        onKeyDown={onKey}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {uploading ? (
          <>
            <ProgressBar size="sm" aria-label="Uploading" className="rc-fileupload__progress" />
            <span className="rc-fileupload__primary" role="status">
              Uploading…
            </span>
          </>
        ) : (
          <>
            <span className="rc-fileupload__icon" aria-hidden="true">
              <Upload size={20} />
            </span>
            <span className="rc-fileupload__primary">{label}</span>
            {hint ? <span className="rc-fileupload__secondary">{hint}</span> : null}
          </>
        )}
      </div>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled || uploading}
        className="rc-fileupload__input"
        onChange={onInputChange}
      />
      {files.length > 0 ? (
        <ul id={`${id}-list`} className="rc-fileupload__list">
          {files.map((f, idx) => (
            <li key={`${f.name}-${idx}`} className="rc-fileupload__item">
              <span className="rc-fileupload__filename">{f.name}</span>
              <span className="rc-fileupload__filesize">{formatBytes(f.size)}</span>
              <IconButton aria-label={`Remove ${f.name}`} icon={<X />} size="sm" onClick={() => removeAt(idx)} />
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <span className="rc-fileupload__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});
